import { useEffect } from 'react';
import * as Yup from 'yup';
import { Box, BoxProps, Button, FormControl } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import FieldErrorMessage from 'src/components/input/FieldErrorMessage';
import { InputLabelInsideFormControl } from 'src/components/input/InputLabelInside';
import Input from 'src/components/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useForgotPassword from './hooks/useForgotPassword';

const validationSchema = Yup.object().shape({
  email: Yup.string().email("This email address isn't valid").required('Required'),
});

interface ForgotPasswordFormProps extends BoxProps {
  onSuccess: (email: string) => void;
}

interface ForgotPasswordFormValues {
  email: string;
}

export default function ForgotPasswordForm({ onSuccess, ...props }: ForgotPasswordFormProps) {
  const { submit, submissionError, isSubmitting } = useForgotPassword(onSuccess);

  const queryParams = useLocation().search;

  const methods = useForm<ForgotPasswordFormValues>({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (queryParams?.includes('email')) {
      const urlEmail = queryParams.split('=')[1];
      methods.setValue('email', decodeURIComponent(urlEmail));
    }
  }, [methods, queryParams]);

  return (
    <Box {...props}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <InputLabelInsideFormControl
            id="email"
            label="Your email address:"
            formLabelProps={{ htmlFor: 'email' }}
            isInvalid={!!methods.formState.errors.email}
            errorMessage={String(methods.formState.errors.email?.message)}
          >
            <Input
              {...methods.register('email')}
              variation="label-inside"
              placeholder="e.g. johnappleseed@example.com"
              autoComplete="email"
            />
          </InputLabelInsideFormControl>
          <FormControl isInvalid={!!submissionError}>
            <FieldErrorMessage>{submissionError}</FieldErrorMessage>
          </FormControl>
          <Button
            width="100%"
            height="54px"
            type="submit"
            isDisabled={!!isSubmitting}
            mt="18px"
            size="lg"
          >
            {isSubmitting ? 'Please wait...' : 'Reset Password'}
          </Button>
        </form>
      </FormProvider>
    </Box>
  );
}
